import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  canPair, canPick, clearBonus, cloneBoard, collapseRows, dealBoard, emptyRound, findHint,
  hasMove, isHunt, isMatch, matchPoints, missPoints, needsRescue, pickPoints, randomSeed, refill,
  remaining, rescueBoard, targetsLeft,
  type Board, type Grade, type LevelConfig, type RoundStats,
} from '@numslearn/shared';
import { useProfile } from '../profile.js';

export type GameStatus = 'ready' | 'playing' | 'cleared' | 'timeup';

export interface MatchEvent {
  kind: 'match' | 'miss';
  /**
   * Board indices at the moment of the tap: two on a pair board, one on a hunt
   * board. Arrays rather than an `a`/`b` pair, so the effect layer and the
   * equation line do not each need to branch on which mechanic is running.
   */
  where: number[];
  /** Stable tile ids — indices shift when a cleared row collapses. */
  ids: number[];
  labels: string[];
  values: number[];
  /**
   * Why a miss was a miss. "These two are right but not connected" is a
   * different lesson from "these two do not make ten", and a nine-year-old
   * deserves to be told which one they just hit.
   */
  reason?: 'rule' | 'connection';
  /** The board was stranded after this match and its tiles were re-dealt. */
  reshuffled?: boolean;
  /** Monotonic, so a repeat of the same tap still re-triggers the effect. */
  nonce: number;
}

export interface GameApi {
  board: Board;
  selected: number | null;
  status: GameStatus;
  stats: RoundStats;
  combo: number;
  secondsLeft: number | null;
  addsLeft: number;
  hintsLeft: number;
  tilesLeft: number;
  /** What is still to be found: every tile on a pair board, the targets on a hunt board. */
  targetsLeft: number;
  /** Targets dealt this board, so a hunt round can show "6 of 11 found". */
  targetsDealt: number;
  lastEvent: MatchEvent | null;
  hintWhere: number[] | null;
  /** Achievement codes unlocked this round, newest last. */
  unlocked: string[];
  movesAvailable: boolean;
  /** The board's deal seed; also picks which built-in picture it hides. */
  seed: number;
  tap: (index: number) => void;
  addRow: () => void;
  useHint: () => void;
  restart: (seed?: number) => void;
}

/**
 * Local game state.
 *
 * In a live class session the server owns the board and re-validates every tap;
 * this hook is deliberately written against the same `canPair` authority so the
 * two cannot drift, and so solo practice works with no backend at all.
 */
export function useGame(config: LevelConfig, grade: Grade, level: number): GameApi {
  const { grantLive, finishRound } = useProfile();
  const [seed, setSeed] = useState(() => randomSeed());
  const [board, setBoard] = useState<Board>(() => dealBoard(config, seed).board);
  const [selected, setSelected] = useState<number | null>(null);
  const [status, setStatus] = useState<GameStatus>('ready');
  const [stats, setStats] = useState<RoundStats>(emptyRound);
  const [combo, setCombo] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(config.timerSec);
  const [lastEvent, setLastEvent] = useState<MatchEvent | null>(null);
  const [hintWhere, setHintWhere] = useState<number[] | null>(null);
  const [unlocked, setUnlocked] = useState<string[]>([]);
  const [targetsDealt, setTargetsDealt] = useState(0);

  const startedAt = useRef<number | null>(null);
  const nonce = useRef(0);
  /**
   * Bases the previous board used, so the next deal leads with different
   * numbers. A teacher asked for this directly: 4 · 8 and 7 · 14 should keep
   * coming round, and 11 · 22 should not lead two boards running.
   */
  const recent = useRef<ReadonlySet<number>>(new Set());

  const reset = useCallback((nextSeed: number) => {
    const dealt = dealBoard(config, nextSeed, recent.current);
    recent.current = dealt.bases;
    setSeed(nextSeed);
    setBoard(dealt.board);
    setTargetsDealt(targetsLeft(dealt.board, config));
    setSelected(null);
    setStatus('ready');
    setStats(emptyRound());
    setCombo(0);
    setSecondsLeft(config.timerSec);
    setLastEvent(null);
    setHintWhere(null);
    setUnlocked([]);
    startedAt.current = null;
  }, [config]);

  // A settings change from the teacher panel deals a fresh board immediately.
  useEffect(() => { reset(randomSeed()); }, [reset]);

  useEffect(() => {
    if (status !== 'playing' || config.timerSec === null) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s === null) return s;
        if (s <= 1) {
          setStatus('timeup');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [status, config.timerSec]);

  const finish = useCallback((finalStats: RoundStats, cleared: boolean) => {
    const done: RoundStats = {
      ...finalStats,
      cleared,
      elapsedMs: startedAt.current ? Date.now() - startedAt.current : 0,
    };
    if (cleared) done.score += clearBonus(done, config);
    setStats(done);
    setStatus(cleared ? 'cleared' : 'timeup');

    // The profile folds the round into the lifetime totals first, so a badge
    // like "first clear" sees the very board that earned it. Rank badges stay
    // the server's to grant once a class session is running.
    const codes = finishRound({ round: done, grade, level });
    if (codes.length) setUnlocked((u) => [...u, ...codes]);
  }, [config, finishRound, grade, level]);

  /** Books a correct answer: score, combo, badges, and the finish check. */
  const credit = useCallback((
    next: Board, gained: number, newCombo: number, event: Omit<MatchEvent, 'nonce' | 'kind'>,
  ) => {
    const nextStats: RoundStats = {
      ...stats,
      score: stats.score + gained,
      matches: stats.matches + 1,
      maxCombo: Math.max(stats.maxCombo, newCombo),
    };
    setBoard(next);
    setCombo(newCombo);
    setStats(nextStats);
    // Combo badges are worth nothing if they arrive after the round: fire
    // them the moment the streak reaches the mark.
    const live = grantLive(nextStats, grade);
    if (live.length) setUnlocked((u) => [...u, ...live]);
    nonce.current += 1;
    setLastEvent({ ...event, kind: 'match', nonce: nonce.current });
    if (targetsLeft(next, config) === 0) finish(nextStats, true);
  }, [config, finish, grade, grantLive, stats]);

  const penalise = useCallback((event: Omit<MatchEvent, 'nonce' | 'kind'>) => {
    setCombo(0);
    setStats((s) => ({ ...s, misses: s.misses + 1, score: Math.max(0, s.score + missPoints(config)) }));
    nonce.current += 1;
    setLastEvent({ ...event, kind: 'miss', nonce: nonce.current });
  }, [config]);

  const tap = useCallback((index: number) => {
    if (status === 'cleared' || status === 'timeup') return;
    const tile = board.tiles[index];
    if (!tile || tile.cleared) return;

    if (startedAt.current === null) {
      startedAt.current = Date.now();
      setStatus('playing');
    }
    setHintWhere(null);

    /* ── hunt: one tile is the whole answer ─────────────────────────────── */
    if (isHunt(config)) {
      const shape = { where: [index], ids: [tile.id], labels: [tile.label], values: [tile.value] };
      if (canPick(board, config, index)) {
        const next = cloneBoard(board);
        next.tiles[index]!.cleared = true;
        credit(next, pickPoints(combo + 1, config, secondsLeft), combo + 1, shape);
      } else {
        penalise({ ...shape, reason: 'rule' });
      }
      return;
    }

    /* ── pair: the first tap arms, the second decides ───────────────────── */
    if (selected === null) { setSelected(index); return; }
    if (selected === index) { setSelected(null); return; }

    const a = selected;
    const b = index;
    const ta = board.tiles[a]!;
    const tb = board.tiles[b]!;
    setSelected(null);
    const shape = {
      where: [a, b], ids: [ta.id, tb.id],
      labels: [ta.label, tb.label], values: [ta.value, tb.value],
    };

    if (canPair(board, config, a, b)) {
      const next = cloneBoard(board);
      next.tiles[a]!.cleared = true;
      next.tiles[b]!.cleared = true;
      // A puzzle board must not collapse — the picture underneath would tear.
      collapseRows(next, config);
      // Stranded with nothing "Add row" can fix: re-deal what is left in place,
      // so the board can still be finished.
      const reshuffled = needsRescue(next, config, config.adds - stats.addsUsed)
        && rescueBoard(next, config, randomSeed());
      credit(next, matchPoints(combo + 1, config, secondsLeft), combo + 1, { ...shape, reshuffled });
    } else {
      penalise({ ...shape, reason: isMatch(ta, tb, config) ? 'connection' : 'rule' });
    }
  }, [board, combo, config, credit, penalise, secondsLeft, selected, stats.addsUsed, status]);

  const addRow = useCallback(() => {
    if (stats.addsUsed >= config.adds || status === 'cleared') return;
    const next = cloneBoard(board);
    if (refill(next, config) === 0) return;
    if (needsRescue(next, config, config.adds - stats.addsUsed - 1)) rescueBoard(next, config, randomSeed());
    setBoard(next);
    setTargetsDealt((n) => n + targetsLeft(next, config) - targetsLeft(board, config));
    setStats((s) => ({ ...s, addsUsed: s.addsUsed + 1 }));
    setSelected(null);
  }, [board, config, stats.addsUsed, status]);

  const useHint = useCallback(() => {
    if (stats.hintsUsed >= config.hints || status === 'cleared') return;
    const where = findHint(board, config);
    if (!where) return;
    setHintWhere(where);
    setStats((s) => ({ ...s, hintsUsed: s.hintsUsed + 1 }));
    window.setTimeout(() => setHintWhere(null), 1400);
  }, [board, config, stats.hintsUsed, status]);

  const restart = useCallback((s?: number) => reset(s ?? randomSeed()), [reset]);

  const tilesLeft = useMemo(() => remaining(board), [board]);
  const left = useMemo(() => targetsLeft(board, config), [board, config]);
  const movesAvailable = useMemo(() => hasMove(board, config), [board, config]);

  return {
    board, selected, status, stats, combo, secondsLeft,
    addsLeft: Math.max(0, config.adds - stats.addsUsed),
    hintsLeft: Math.max(0, config.hints - stats.hintsUsed),
    tilesLeft, targetsLeft: left, targetsDealt,
    lastEvent, hintWhere, unlocked, movesAvailable,
    seed, tap, addRow, useHint, restart,
  };
}

/** Seed is exposed for the future session layer, which deals boards by seed. */
export { randomSeed };
